// src/pages/Clinics/index.js
//
// Klinikler (admin) — sistemdeki tüm klinikler.
// Düz liste (docs/design-principles.md §3): tek arama → durum çipleri (sayılı) →
// çerçevesiz tablo. Satır tıklanınca klinik detay sayfasına gider; oluşturma
// başlık şeridindeki "Klinik Oluştur" ile sağdan drawer'da (iki adım: bilgiler →
// e-posta doğrulama). Arama/çip/sıralama sessionStorage'da: detaydan geri dönünce
// liste aynı görünümle açılır.

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  ButtonBase,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import { FaClinicMedical } from "react-icons/fa";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useClinicTheme } from "../../theme";
import { profilesLabel } from "../../constants/clinicModules";
import { resolveLogo } from "../../utils/clinicLogo";
import { ActionButton, formatPhoneDisplay } from "../../components/atoms";
import { DataTableCard } from "../../components/organisms";
import {
  ClinicRecordListLayout,
  ClickableRow,
  DateCell,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SkeletonRows,
  SortHead,
  StatusPill,
  TextCell,
  cellSx,
  mainText,
  subText,
  useClientList,
} from "../../components/organisms/ClinicRecordList";
import ClinicFormDrawer from "./ClinicFormDrawer";
import useClinics from "./useClinics";
import {
  CLINIC_DEFAULT_DIR,
  CLINIC_SORTERS,
  createdOf,
  locationOf,
  matchesClinic,
} from "./clinicListModel";

const COLS = 6;

const STATUS = {
  active: { label: "Aktif", tone: "success" },
  inactive: { label: "Pasif", tone: "neutral" },
};

const statusOf = (c) =>
  c?.status === "inactive"
    ? "inactive"
    : c?.status === "active"
      ? "active"
      : "other";

/** Klinik hücresi: logo (yoksa kliniğin kendi renginde baş harf) + ad + "#1042 · Kurumsal". */
function ClinicCell({ clinic, fallbackColor }) {
  const logo = resolveLogo(clinic?.logoIconUrl);
  const color = clinic?.themeColor || fallbackColor;
  const meta = [
    clinic?.puserNo != null ? `#${clinic.puserNo}` : null,
    clinic?.isCorporate ? "Kurumsal" : "Bireysel",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <TableCell sx={{ ...cellSx, ...EDGE_L }}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
      >
        {logo ? (
          <Avatar
            src={logo}
            alt=""
            variant="rounded"
            sx={{ width: 34, height: 34, bgcolor: "transparent" }}
          />
        ) : (
          <Avatar
            variant="rounded"
            sx={{
              width: 34,
              height: 34,
              fontSize: 12.5,
              fontWeight: 600,
              bgcolor: alpha(color, 0.14),
              color,
            }}
          >
            {(clinic?.name || "K").charAt(0).toLocaleUpperCase("tr")}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...mainText, fontWeight: 600 }} noWrap>
            {clinic?.name || "—"}
          </Typography>
          <Typography
            sx={{ ...subText, fontVariantNumeric: "tabular-nums" }}
            noWrap
          >
            {meta}
          </Typography>
        </Box>
      </Box>
    </TableCell>
  );
}

export default function Clinics() {
  const vm = useClinics();
  const navigate = useNavigate();
  const { palette } = useClinicTheme();
  const canCreate = useHasPermission("admin-clinics:create");

  const list = useClientList(vm.rows, {
    segmentOf: statusOf,
    matches: matchesClinic,
    sorters: CLINIC_SORTERS,
    defaultDir: CLINIC_DEFAULT_DIR,
    initialSort: "date",
    persistKey: "clinicList:view",
  });

  const segments = useMemo(
    () => [
      { value: "all", label: `Tümü · ${list.counts.all || 0}` },
      { value: "active", label: `Aktif · ${list.counts.active || 0}` },
      { value: "inactive", label: `Pasif · ${list.counts.inactive || 0}` },
    ],
    [list.counts],
  );

  const busy = vm.isLoading && !vm.loaded;
  // Boş durumda satır içi aksiyon bağlantısı (design-principles §5)
  const createLink = (label) =>
    canCreate ? (
      <ButtonBase
        onClick={vm.form.openCreate}
        sx={{
          font: "inherit",
          color: palette.primary,
          fontWeight: 600,
          textDecoration: "underline",
          textUnderlineOffset: 3,
          verticalAlign: "baseline",
        }}
      >
        {label}
      </ButtonBase>
    ) : null;

  const empty = vm.errorMsg
    ? { title: "Klinikler yüklenemedi", message: vm.errorMsg }
    : list.searchText
      ? {
          title: "Klinik bulunamadı",
          message: (
            <>
              “{list.searchText}” aramanızla eşleşen klinik yok. Aramayı
              değiştirin
              {canCreate ? (
                <> veya {createLink("yeni klinik oluşturun")}</>
              ) : null}
              .
            </>
          ),
        }
      : list.segment !== "all"
        ? {
            title: "Bu filtrede klinik yok",
            message: `${STATUS[list.segment]?.label || "Bu"} durumda klinik bulunmuyor.`,
          }
        : {
            title: "Henüz klinik yok",
            message: canCreate ? (
              <>İlk kliniği {createLink("oluşturarak")} başlayın.</>
            ) : (
              "Sistemde kayıtlı klinik bulunmuyor."
            ),
          };

  const sortProps = { sort: list.sort, dir: list.dir, onSort: list.onSort };

  return (
    <>
      <ClinicRecordListLayout
        icon={<FaClinicMedical size={20} />}
        title="Klinikler"
        subtitle="Sistemdeki klinikler; detay için satıra tıklayın"
        action={
          canCreate ? (
            <ActionButton
              variant="add"
              palette={palette}
              label="Klinik Oluştur"
              onClick={vm.form.openCreate}
              disabled={!vm.token}
            />
          ) : null
        }
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder="Klinik adı, e-posta, puser no veya il ile ara"
        segments={segments}
        segment={list.segment}
        onSegmentChange={list.setSegment}
        segmentAriaLabel="Klinik durumu"
      >
        <DataTableCard
          loading={Boolean(busy)}
          variant="flat"
          empty={!busy && list.filtered.length === 0}
          emptyIcon={<FaClinicMedical />}
          emptyTitle={empty.title}
          emptyMessage={empty.message}
          page={list.page}
          rowsPerPage={list.rowsPerPage}
          totalCount={list.filtered.length}
          onPageChange={list.onPageChange}
          onRowsPerPageChange={list.onRowsPerPageChange}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        >
          <Table
            stickyHeader
            size="small"
            sx={{ tableLayout: "fixed", minWidth: 1000 }}
          >
            <TableHead>
              <TableRow>
                <SortHead
                  field="name"
                  label="Klinik"
                  {...sortProps}
                  sx={{ ...EDGE_L, width: "28%" }}
                />
                <PlainHead sx={{ width: "14%" }}>Klinik Türü</PlainHead>
                <SortHead
                  field="location"
                  label="Konum"
                  {...sortProps}
                  sx={{ width: "17%" }}
                />
                <PlainHead sx={{ width: "21%" }}>İletişim</PlainHead>
                <SortHead
                  field="date"
                  label="Oluşturulma"
                  defaultDir="desc"
                  {...sortProps}
                  sx={{ width: "11%" }}
                />
                <PlainHead sx={{ ...EDGE_R, width: "9%" }}>Durum</PlainHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {busy ? (
                <SkeletonRows
                  rows={Math.min(10, list.rowsPerPage)}
                  cols={COLS}
                />
              ) : list.pagedRows.length ? (
                list.pagedRows.map((c) => {
                  const st = STATUS[c.status];
                  return (
                    <ClickableRow
                      key={c._id}
                      onClick={() => navigate(`/Klinikler/${c._id}`)}
                      palette={palette}
                    >
                      <ClinicCell clinic={c} fallbackColor={palette.primary} />
                      <TextCell main={profilesLabel(c.profiles)} />
                      <TextCell
                        main={locationOf(c)}
                        sub={c.address?.fullAddress || null}
                        tooltip={c.address?.fullAddress || ""}
                      />
                      <TextCell
                        main={c.email || null}
                        sub={c.phone ? formatPhoneDisplay(c.phone) : null}
                      />
                      <DateCell value={createdOf(c)} />
                      <TableCell sx={{ ...cellSx, ...EDGE_R }}>
                        <StatusPill
                          label={st?.label || c.status || "—"}
                          tone={st?.tone}
                          palette={palette}
                        />
                      </TableCell>
                    </ClickableRow>
                  );
                })
              ) : (
                <FillerRow cols={COLS} />
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </ClinicRecordListLayout>

      <ClinicFormDrawer
        open={vm.form.drawerOpen}
        onClose={vm.form.closeDrawer}
        form={vm.form.form}
        setField={vm.form.setField}
        onSubmit={vm.form.handleSubmit}
        canSubmit={vm.form.canSubmit}
        isCreate={vm.form.isCreate}
        isSendingCode={vm.form.isSendingCode}
        isVerifying={vm.form.isVerifying}
        isUpdateLoading={vm.isUpdateLoading}
        token={vm.token}
        step={vm.form.step}
        verificationCode={vm.form.verificationCode}
        setVerificationCode={vm.form.setVerificationCode}
        codeError={vm.form.codeError}
        resendCountdown={vm.form.resendCountdown}
        onVerifyAndCreate={vm.form.handleVerifyAndCreate}
        onResendCode={vm.form.handleResendCode}
        onBackToForm={vm.form.handleBackToForm}
      />
    </>
  );
}
